import { DatabaseType } from '../../offline-first';
import { DocumentRecord, DocumentRecordData, Identifier, Record, Version } from '../../offline-first';
import { first, get, isNil, isNumber, isString, parseInt, round } from 'lodash';
import moment from 'moment';
import { Exercise, Tier } from './exercise-program-document';

export const rpeOptions = [
  { value: 0, description: 'Rest' },
  { value: 1, description: 'Very, Very Easy' },
  { value: 2, description: 'Easy' },
  { value: 3, description: 'Moderate' },
  { value: 4, description: 'Somewhat Hard' },
  { value: 5, description: 'Hard' },
  { value: 6, description: 'Hard' },
  { value: 7, description: 'Very Hard' },
  { value: 8, description: 'Very Hard' },
  { value: 9, description: 'Very Hard' },
  { value: 10, description: 'Maximal' },
];

export interface Plan extends Record {
  type: 'Plan';
  version: Version;
  exercise_program_id: Identifier;
  exercise_program_version: Version;
  notes?: string;
  num_weeks: number;
  start_date?: string;
  exercises: Exercise[];
  phases: Phase[];
  workouts: Workout[];
}

export interface Phase extends Record {
  exercise_program_phase_id?: Identifier;
  start_week: number | string;
  start_day: number | string;
  end_week: number | string;
  end_day: number | string;
  color?: string;
}

export interface Workout extends Record {
  workout_type_id?: Identifier;
  restrict_label_id?: Identifier;
  schedule: Schedule;
  time?: string;
  tiers: PlannedTier[];
  complete?: boolean;
  est_completion_time_ms?: number;
  est_rpe?: number;
  version: number;
}

export interface Schedule {
  day: number[];
  week: number[];
}

export interface PlannedTier extends Record {
  notes?: string;
  order: number;
  rows: PlannedTierRow[];
  circuit?: {
    num_rounds: number;
    rest_time_ms: number;
  };
}

export interface PlannedTierRow {
  id: Identifier;
  exercise_id: Identifier;
  exercise_name: string;
  order?: number;
  parameter_selections?: object;
  notes?: string;
}

export class PlanDocument extends DocumentRecord<Plan> {
  static recordName = 'PlanDocument';
  static objectStoreName = 'planDocuments';

  constructor(data?: DocumentRecordData<Plan>) {
    super(data);
  }
}

export class PlanDocumentCache extends PlanDocument {
  static recordName = 'PlanDocumentCache';
  static objectStoreName = 'planDocumentsCache';
  static fields = ['version', 'userUUID', 'document', 'lastSynced'];

  static databaseType = DatabaseType.Document;

  syncedAt: moment.Moment;

  constructor(plan: PlanDocument) {
    super({ key: plan.key, document: plan.document, version: plan.version });

    this.syncedAt = moment();
    this.createdAt = plan.createdAt;
    this.updatedAt = plan.updatedAt;
    this.deletedAt = plan.deletedAt;
  }
}

export class PlanDocumentUtils {
  static getAcronym(name: string): string {
    const words = name ? name.trim().split(/\s+/) : [];
    let toReturn = '';
    words.forEach(word => {
      const letter = word.substring(0, 1);
      if (letter.match(/[a-zA-Z0-9]/)) {
        toReturn += letter;
      }
    });

    toReturn = toReturn || (name.replace(/\s/g, '') + '--').substr(0, 2);

    return toReturn.toUpperCase().substr(0, 4);
  }

  static getSchedule(referenceDate: moment.Moment, date: moment.Moment): { day: number; week: number } {
    let week = date.week() - referenceDate.week();
    if (date.week() - referenceDate.week() < 0) {
      week += 52;
    }

    let day = date.day() - referenceDate.day();
    if (day < 0) {
      day += 7;
      week--;
    }

    return {
      day,
      week,
    };
  }

  static getDate(referenceDate: moment.Moment, day: number | string, week: number | string): moment.Moment {
    const parsedDay = isString(day) ? parseInt(day, 10) : day;
    const parsedWeek = isString(week) ? parseInt(week, 10) : week;

    let adjustedDay = parsedDay + referenceDate.day();
    let adjustedWeek = parsedWeek;
    if (adjustedDay > 7) {
      adjustedDay -= 7;
      adjustedWeek++;
    }

    const dayDiff = adjustedDay - referenceDate.day();
    const date = referenceDate.clone();
    date.add(adjustedWeek, 'w');
    date.add(dayDiff, 'd');

    return date;
  }

  /**
   * Searches the provided set of workouts and returns the first instance of the workout
   * matching the schedule parameters
   * @param workouts set of workouts to search
   * @param schedule schedule of workout to find
   */
  static findWorkout(workouts: Workout[], schedule: { week: number; day: number }): Workout {
    if (workouts) {
      const allWorkouts = this.findWorkouts(workouts, schedule);
      if (allWorkouts && allWorkouts.length > 0) {
        return first(allWorkouts);
      }
    }

    return null;
  }

  /**
   * Searches the provided set of workouts and returns all workouts that match the schedule parameter
   * @param workouts set of workouts to search
   * @param schedule schedule of workouts to find
   */
  static findWorkouts(workouts: Workout[], schedule: { week: number; day: number }): Workout[] {
    if (workouts) {
      return workouts.filter(workout => {
        if (workout.hasOwnProperty('schedule')) {
          // tslint:disable-next-line: prefer-for-of
          for (let i = 0; i < workout.schedule.week.length; i++) {
            // tslint:disable-next-line: prefer-for-of
            for (let j = 0; j < workout.schedule.day.length; j++) {
              if (workout.schedule.week[i] === schedule.week && workout.schedule.day[j] === schedule.day) {
                return true;
              }
            }
          }
        }

        return false;
      });
    }

    return null;
  }

  static findPhase(phases: Phase[], schedule: { week: number; day: number }): Phase | undefined {
    if (phases) {
      return phases.find(phase => {
        const startWeek = Number(phase.start_week);
        const endWeek = Number(phase.end_week);
        const startDay = Number(phase.start_day);
        const endDay = Number(phase.end_day);
        
        return (
          (startWeek < schedule.week && endWeek > schedule.week) ||
          (startWeek === schedule.week && endWeek > schedule.week && startDay <= schedule.day) ||
          (endWeek === schedule.week && startWeek < schedule.week && endDay >= schedule.day) ||
          (startWeek === schedule.week &&
            endWeek === schedule.week &&
            startDay <= schedule.day &&
            endDay >= schedule.day)
        );
      });
    }
    return undefined;
  }

  static compare(a: Record, b: Record): boolean {
    return (!a && !b) || (a && b && a.id === b.id && a.name === b.name);
  }

  static calcPercent1RM(workout: Workout) {
    const loadParameter = 'load';
    let percent1RM = 0;
    if (workout && workout.tiers) {
      const tier3 = workout.tiers.find(tier => tier.order === 3);
      let loadSum = 0;

      if (tier3) {
        const totalLoads = this.numOfRowsWithLoads(tier3.rows);

        tier3.rows.forEach((row, index) => {
          if (index === 0) {
            const params = row.parameter_selections;
            if (params && !isNil(get(params, loadParameter))) {
              if (isString(params[loadParameter])) {
                loadSum += +params[loadParameter].substring(0, params[loadParameter].length - 1);
              } else if (isNumber(params[loadParameter]) && params[loadParameter] > -1) {
                loadSum += params[loadParameter];
              }
            }
          }
        });

        if (totalLoads > 0) {
          percent1RM = round(loadSum / totalLoads);
        }
      }
    }

    return percent1RM;
  }

  static numOfRowsWithLoads(rows: PlannedTierRow[]) {
    let sum = 0;

    rows.forEach((row, index) => {
      if (index === 0 && row.parameter_selections) {
        if (row.parameter_selections.hasOwnProperty('load')) {
          sum++;
        }
      }
    });

    return sum;
  }

  static getHex(colorClass?: string) {
    if (!colorClass) {
      return '#818181';
    }

    let elementUsingStyling = document.querySelector('.' + colorClass);
    let createdTempElement = false;

    // if there wasn't anything using this color yet, create a placeholder element
    if (!elementUsingStyling) {
      elementUsingStyling = document.createElement('div');
      elementUsingStyling.classList.add(colorClass);
      document.body.appendChild(elementUsingStyling);
      createdTempElement = true;
    }

    const colorCode = window.getComputedStyle(elementUsingStyling).getPropertyValue('background-color');

    if (createdTempElement) {
      document.body.removeChild(elementUsingStyling);
    }

    return colorCode.indexOf('rgb') === 0 ? this.rgbToHex(colorCode) : colorCode;
  }

  static rgbToHex(rgb) {
    rgb = rgb.match(/^rgba?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?/i);
    return rgb && rgb.length === 4
      ? '#' +
          ('0' + parseInt(rgb[1], 10).toString(16)).slice(-2) +
          ('0' + parseInt(rgb[2], 10).toString(16)).slice(-2) +
          ('0' + parseInt(rgb[3], 10).toString(16)).slice(-2)
      : '';
  }

  static sortTiers(a: Tier | PlannedTier | PlannedTierRow, b: Tier | PlannedTier | PlannedTierRow) {
    return a.order > b.order ? 1 : a.order === b.order ? 0 : -1;
  }
}
